import { usePlugins } from '@/contexts/PluginContext'
import { CheckCircle2, Download, Settings, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

import { PageHeader } from '@/components/atoms'

import { PluginDefinition } from '@/types/plugin'

export const PluginMarketplace = () => {
  const { availablePlugins, isPluginInstalled, installPlugin, uninstallPlugin } = usePlugins()
  const navigate = useNavigate()
  const [busy, setBusy] = useState<string | null>(null)

  const settingsPath = (plugin: PluginDefinition) => plugin.navItems?.[0]?.path

  const handleInstall = async (plugin: PluginDefinition) => {
    setBusy(plugin.id)
    const ok = await installPlugin(plugin.id)
    setBusy(null)
    if (!ok) {
      toast.error(`Could not install ${plugin.name}`)
      return
    }
    toast.success(`${plugin.name} installed`)
    const path = settingsPath(plugin)
    if (path) navigate(path)
  }

  const handleUninstall = async (plugin: PluginDefinition) => {
    if (!confirm(`Are you sure you want to uninstall ${plugin.name}?`)) return
    setBusy(plugin.id)
    const ok = await uninstallPlugin(plugin.id)
    setBusy(null)
    if (ok) toast.success(`${plugin.name} uninstalled`)
    else toast.error(`Could not uninstall ${plugin.name}`)
  }

  return (
    <div>
      <PageHeader title="Plugins" description="Add features to your store." />

      {!availablePlugins.length && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">No plugins are available yet.</CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {availablePlugins.map((plugin) => {
          const isInstalled = isPluginInstalled(plugin.id)
          const path = settingsPath(plugin)

          return (
            <Card key={plugin.id} className="flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <CardTitle>{plugin.name}</CardTitle>
                    <CardDescription>
                      v{plugin.version} · by {plugin.author}
                    </CardDescription>
                  </div>
                  {isInstalled && (
                    <Badge tone="success">
                      <CheckCircle2 className="size-3" aria-hidden="true" />
                      Installed
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-4">
                <p className="flex-1 text-sm text-text-2">{plugin.description}</p>

                {isInstalled ? (
                  <div className="flex gap-2">
                    {path && (
                      <Button onClick={() => navigate(path)}>
                        <Settings />
                        Open
                      </Button>
                    )}
                    <Button variant="secondary" disabled={busy === plugin.id} onClick={() => handleUninstall(plugin)}>
                      <Trash2 />
                      Uninstall
                    </Button>
                  </div>
                ) : (
                  <Button className="self-start" disabled={busy === plugin.id} onClick={() => handleInstall(plugin)}>
                    <Download />
                    Install
                  </Button>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
